import React, { useState, useEffect } from "react";
import { useHistory, Link } from "react-router-dom";
import { createTask, subscribeToLabels, listTaskUsers } from "../../firebase/tasks";
import { useSession } from "../../firebase/UserProvider";
import { LabelMentionInput } from "../../components/tms/LabelMentionInput";
import { TaskToolbar } from "../../components/tms/TaskToolbar";
import TaskSearchInput from "../../components/tms/TaskSearchInput";

export default function CreateTask({ inline = false, onClose, onSuccess }) {
  const history = useHistory();
  const { user } = useSession();
  const isPageRoute = window.location.pathname === "/tasks/new";
  const [isExpanded, setIsExpanded] = useState(inline ? false : isPageRoute);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [assigneeUid, setAssigneeUid] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [recurrence, setRecurrence] = useState(null);

  const [users, setUsers] = useState([]);
  const [labels, setLabels] = useState([]);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let unsubLabels;
    listTaskUsers().then(setUsers).catch(console.error);
    unsubLabels = subscribeToLabels(setLabels);
    return () => unsubLabels && unsubLabels();
  }, []);

  const handleOpenLabels = () => {
    const textarea = document.querySelector(".title-mention-input textarea");
    if (!textarea) return;
    textarea.focus();
    const currentVal = textarea.value;
    const needsSpace = currentVal.length > 0 && !/\s$/.test(currentVal);
    const appendStr = (needsSpace ? " " : "") + "@";
    const startPos = textarea.selectionStart;
    const newVal = currentVal.substring(0, startPos) + appendStr + currentVal.substring(textarea.selectionEnd);
    setTitle(newVal);
    const newPos = startPos + appendStr.length;
    setTimeout(() => textarea.setSelectionRange(newPos, newPos), 0);
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setPriority("Medium");
    setAssigneeUid("");
    setDueDate("");
    setRecurrence(null);
    setError("");
  };

  const handleCancel = () => {
    resetForm();
    if (inline) {
      setIsExpanded(false);
      onClose?.();
    } else {
      history.push("/tasks");
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!title.trim()) return setError("Task title is required.");

    setIsSubmitting(true);
    setError("");

    try {
      const selectedLabels = [];
      const titleWords = title.split(/\s+/);
      const cleanTitleWords = [];

      titleWords.forEach((word) => {
        if (word.startsWith("@")) {
          const lName = word.substring(1);
          const matched = labels.find((l) => l.name.toLowerCase() === lName.toLowerCase());
          if (matched) {
            if (!selectedLabels.includes(matched.name)) selectedLabels.push(matched.name);
          } else {
            cleanTitleWords.push(word);
          }
        } else {
          cleanTitleWords.push(word);
        }
      });

      const cleanTitle = cleanTitleWords.join(" ").trim();
      if (!cleanTitle) {
        setIsSubmitting(false);
        return setError("Task title cannot be empty after removing label tags.");
      }

      const taskData = {
        title: cleanTitle,
        description: description.trim(),
        priority,
        status: "Open",
        assigneeUid: assigneeUid || null,
        dueDate: dueDate || null,
        labels: selectedLabels,
        recurrence: recurrence || null,
        createdByUid: user?.uid || "system",
        updatedByUid: user?.uid || "system",
      };

      const result = await createTask(taskData);
      resetForm();
      
      if (inline) {
        setIsExpanded(false);
        onSuccess?.(result);
      } else {
        history.push("/tasks");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to create task.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formCard = (
    <div className="border border-gray-200 rounded-lg p-3 shadow-sm bg-white focus-within:border-gray-300 transition-all text-left">
      {error && <div className="mb-2 text-xs font-semibold text-red-500 bg-red-50 p-2 rounded">⚠️ {error}</div>}
      
      <div className="flex items-start justify-between gap-2 title-mention-input">
        <div className="flex-1 [&>div]:!border-none [&>div]:!ring-0 [&>div]:!shadow-none [&_.bg-gray-50]:!hidden [&_textarea]:!text-base [&_textarea]:!font-semibold [&_textarea]:!p-0 [&_textarea]:!min-h-[32px] [&_textarea]:!h-auto [&_textarea]:!resize-none [&_textarea]:!placeholder-gray-400 [&_textarea]:!text-gray-800">
          <LabelMentionInput
            value={title}
            onChange={setTitle}
            onSubmit={handleSubmit}
            users={labels.map((l) => ({ uid: l.id, name: l.name }))}
            placeholder="Task name"
          />
        </div>
        <div className="flex items-center gap-2.5 text-gray-400 select-none text-xl pt-0.5 pr-1">
          <span className="cursor-pointer hover:text-gray-600">≡</span>
          <span className="cursor-pointer hover:text-gray-600">🎙</span>
        </div>
      </div>

      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description"
        className="w-full text-xs text-gray-700 placeholder-gray-400 focus:outline-none resize-none bg-transparent min-h-[48px] mt-1.5"
        rows={2}
      />

      <hr className="border-gray-100 my-2" />

      <div className="overflow-x-auto scrollbar-none -mx-3 px-3 [&>div]:flex-nowrap [&>div]:overflow-x-auto [&>div]:scrollbar-none [&>div]:py-1">
        <TaskToolbar
          dueDate={dueDate}
          onChangeDate={setDueDate}
          priority={priority}
          onChangePriority={setPriority}
          assigneeUid={assigneeUid}
          onChangeAssignee={setAssigneeUid}
          users={users}
          onOpenLabels={handleOpenLabels}
          recurrence={recurrence}
          onChangeRecurrence={setRecurrence}
        />
      </div>

      <div className="flex items-center justify-between border-t border-gray-100 pt-3 mt-2">
        <button type="button" className="text-xs font-bold text-gray-500 hover:text-gray-700 flex items-center gap-1 cursor-pointer bg-gray-50 px-2.5 py-1 rounded border border-gray-200">
          <span>📥</span><span>Inbox ▾</span>
        </button>
        <div className="flex items-center gap-2">
          <button type="button" onClick={handleCancel} className="text-xs font-bold text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg transition-colors cursor-pointer">Cancel</button>
          <button type="button" disabled={isSubmitting || !title.trim()} onClick={handleSubmit} className="bg-[#db4035] hover:bg-[#b8332a] text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
            {isSubmitting ? "Adding..." : "Add task"}
          </button>
        </div>
      </div>
    </div>
  );

  if (inline) {
    return (
      <div className="w-full">
        {!isExpanded ? (
          <button type="button" onClick={() => setIsExpanded(true)} className="flex items-center gap-2.5 px-3 py-2 text-gray-500 hover:text-[#db4035] font-semibold text-sm transition-all rounded-lg hover:bg-[#db4035]/5 cursor-pointer w-full text-left">
            <span className="text-[#db4035] text-lg font-bold">+</span><span>Add task</span>
          </button>
        ) : formCard}
      </div>
    );
  }

  return (
    <div className="container-fluid min-h-screen py-8 pb-36 px-4 md:px-8 bg-[var(--theme-color-canvas)]">
      <div className="max-w-xl mx-auto mb-6">
        <Link to="/tasks" className="inline-flex items-center text-sm font-bold mb-4 text-[var(--theme-color-primary)] hover:opacity-80" style={{ textDecoration: "none" }}>
          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
          Back to Tasks List
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl font-extrabold tracking-tight text-[var(--theme-color-ink)]">Create New Task</h1>
          <TaskSearchInput />
        </div>
      </div>
      <div className="max-w-xl mx-auto">{formCard}</div>
    </div>
  );
}
